import { useRef, useState, useMemo } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { useClickOutside } from '../hooks/useClickOutside';
import { cn } from '../utils/cn';

export interface CheckboxSelectOption {
  value: string;
  label: string;
  group?: string;
  disabled?: boolean;
}

export interface CheckboxSelectProps {
  label?: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: CheckboxSelectOption[];
  placeholder?: string;
  searchable?: boolean;
  showSelectAll?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  width?: number | string;
}

export function CheckboxSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select options',
  searchable = true,
  showSelectAll = true,
  error,
  disabled,
  className,
  width = 260,
}: CheckboxSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const groups = useMemo(() => {
    const map = new Map<string, CheckboxSelectOption[]>();
    for (const o of filtered) {
      const key = o.group ?? '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(o);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const toggle = (v: string) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const allVisible = filtered.filter((o) => !o.disabled).map((o) => o.value);
  const allSelected = allVisible.length > 0 && allVisible.every((v) => value.includes(v));

  const toggleAll = () => {
    if (allSelected) onChange(value.filter((v) => !allVisible.includes(v)));
    else onChange(Array.from(new Set([...value, ...allVisible])));
  };

  const displayLabel =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? options.find((o) => o.value === value[0])?.label ?? placeholder
        : `${value.length} selected`;

  return (
    <div className={cn('flex w-full flex-col gap-1', className)} ref={ref}>
      {label && <label className="text-xs font-medium text-content-secondary">{label}</label>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 text-sm transition-colors',
          open ? 'border-primary' : 'border-border',
          error && 'border-danger',
          disabled && 'opacity-50 cursor-not-allowed',
          'bg-surface-card text-content-primary hover:bg-surface-card-hover'
        )}
      >
        <span className={cn('truncate', value.length === 0 && 'text-content-muted')}>
          {displayLabel}
        </span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-content-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="relative">
          <div
            style={{ width }}
            className="absolute left-0 top-1 z-50 rounded-md border border-border bg-surface-card shadow-xl"
          >
            {searchable && (
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <Search className="h-3.5 w-3.5 text-content-muted" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted"
                />
              </div>
            )}

            {showSelectAll && filtered.length > 0 && (
              <button
                type="button"
                onClick={toggleAll}
                className="flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-xs font-medium text-content-secondary hover:bg-surface-card-hover"
              >
                <Box checked={allSelected} />
                {allSelected ? 'Unselect all' : 'Select all'}
              </button>
            )}

            <div className="max-h-60 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-xs text-content-muted">No matches</div>
              )}
              {groups.map(([group, items]) => (
                <div key={group || 'default'}>
                  {group && (
                    <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-content-muted">
                      {group}
                    </div>
                  )}
                  {items.map((opt) => {
                    const checked = value.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors',
                          opt.disabled && 'opacity-40 cursor-not-allowed',
                          checked
                            ? 'bg-primary-light text-primary font-medium'
                            : 'text-content-secondary hover:bg-surface-card-hover'
                        )}
                      >
                        <Box checked={checked} />
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          disabled={opt.disabled}
                          onChange={() => toggle(opt.value)}
                        />
                        <span className="flex-1 truncate">{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}

function Box({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
        checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong'
      )}
    >
      {checked && <Check className="h-3 w-3" />}
    </span>
  );
}

export default CheckboxSelect;
